import { ActionIconButton } from '@/shared/components/ui/ActionIconButton';
import { Button } from '@/shared/components/ui/Button';
import { useClientPagination } from '@/shared/hooks/useClientPagination';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { confirm } from '@/shared/lib/confirm';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Search, Trash2, User, Users, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  type AccessGroupRow,
  type UserRow,
  createAccessGroup,
  deleteAccessGroup,
  getAccessGroupMembers,
  listAccessGroups,
  listUsers,
  setAccessGroupMembers,
  updateAccessGroup,
} from '../api';

export function GruposAcessoTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['access-groups'], queryFn: listAccessGroups });
  const [editing, setEditing] = useState<AccessGroupRow | 'new' | null>(null);
  const [managing, setManaging] = useState<AccessGroupRow | null>(null);
  const all = q.data ?? [];
  const { visible, hasMore, sentinelRef } = useClientPagination(all);

  const delMut = useMutation({
    mutationFn: deleteAccessGroup,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['access-groups'] }),
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.problem.title : 'Falha ao excluir', {
        ...(err instanceof ApiError && err.problem.detail
          ? { description: err.problem.detail }
          : {}),
      }),
  });

  return (
    <section className="flex h-full flex-col overflow-hidden">
      <header className="mb-5 flex flex-shrink-0 items-center justify-between">
        <div>
          <h2
            className="font-bold text-[20px] text-n-900"
            style={{ letterSpacing: '-.02em', marginBottom: '4px' }}
          >
            Grupos de Acesso
          </h2>
          <p className="text-[13px] text-n-500">
            Segmente usuários (ex.: por loja). Use os grupos pra limitar quais mensagens rápidas
            cada um vê.
          </p>
        </div>
        <Button onClick={() => setEditing('new')}>
          <Plus aria-hidden="true" />
          Novo grupo
        </Button>
      </header>

      {!q.isLoading && all.length > 0 && (
        <p className="mb-2 flex-shrink-0 text-[12px] text-n-500">
          {visible.length === all.length
            ? `${all.length} ${all.length === 1 ? 'grupo' : 'grupos'}`
            : `Mostrando ${visible.length} de ${all.length} grupos`}
        </p>
      )}
      <div className="min-h-0 flex flex-col overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
            <tr>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Nome
              </th>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Descrição
              </th>
              <th className="w-[120px] px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Membros
              </th>
              <th className="w-[140px] px-[14px] py-[10px] text-right text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center">
                  <Loader2 size={18} className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!q.isLoading && all.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-12 text-center text-n-500">
                  Nenhum grupo de acesso cadastrado.
                </td>
              </tr>
            )}
            {visible.map((g) => (
              <tr key={g.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                <td className="px-[14px] py-[11px] font-medium text-n-900">{g.name}</td>
                <td className="px-[14px] py-[11px] text-n-700">
                  {g.description ? (
                    <span className="block max-w-[420px] truncate" title={g.description}>
                      {g.description}
                    </span>
                  ) : (
                    <span className="text-n-400">—</span>
                  )}
                </td>
                <td className="px-[14px] py-[11px]">
                  <span className="inline-flex items-center gap-1.5 text-n-700">
                    <Users size={14} strokeWidth={1.75} className="text-n-400" />
                    {g.memberCount}
                  </span>
                </td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex justify-end gap-1">
                    <ActionIconButton
                      icon={Users}
                      label="Gerenciar membros"
                      onClick={() => setManaging(g)}
                    />
                    <ActionIconButton
                      icon={Pencil}
                      label="Editar"
                      tone="brand"
                      onClick={() => setEditing(g)}
                    />
                    <ActionIconButton
                      icon={Trash2}
                      label="Apagar"
                      tone="danger"
                      onClick={async () => {
                        const ok = await confirm({
                          title: `Apagar "${g.name}"?`,
                          description:
                            'Os contatos, atendentes e canais vinculados a este grupo perderão o vínculo. Nada além disso é apagado. Esta ação não pode ser desfeita.',
                          confirmText: 'Apagar',
                          variant: 'danger',
                        });
                        if (ok) delMut.mutate(g.id);
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {hasMore && (
              <tr ref={sentinelRef}>
                <td colSpan={4} className="py-4 text-center text-[12px] text-n-500">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Carregando mais…
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <AccessGroupFormModal
          group={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {managing && <AccessGroupMembersModal group={managing} onClose={() => setManaging(null)} />}
    </section>
  );
}

function AccessGroupFormModal({
  group,
  onClose,
}: {
  group: AccessGroupRow | null;
  onClose: () => void;
}) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  // Erro inline no campo nome (409 = nome duplicado).
  const [nameError, setNameError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      const trimmedDesc = description.trim();
      if (group) {
        await updateAccessGroup(group.id, {
          name: name.trim(),
          description: trimmedDesc,
        });
      } else {
        await createAccessGroup({
          name: name.trim(),
          ...(trimmedDesc ? { description: trimmedDesc } : {}),
        });
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['access-groups'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setNameError('Já existe um grupo com esse nome.');
          return;
        }
        toast.error(err.problem.title, { description: err.problem.detail });
      } else {
        toast.error('Erro ao salvar', { description: String(err) });
      }
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-[420px] rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[16px]">{group ? 'Editar grupo' : 'Novo grupo'}</h3>
          <button type="button" onClick={onClose} className="text-n-500" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>
        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Nome</span>
            <input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
              className={`rounded-md border px-3 py-2 text-[13px] outline-none focus:border-brand-500 ${
                nameError ? 'border-danger-500' : 'border-n-200'
              }`}
              placeholder="Ex.: Loja Centro"
              autoFocus
            />
            {nameError && <span className="text-[11px] text-danger-600">{nameError}</span>}
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Descrição (opcional)</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[80px] resize-y rounded-md border border-n-200 px-3 py-2 text-[13px] outline-none focus:border-brand-500"
              placeholder="Pra que serve este grupo…"
            />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !name.trim()}>
            {mut.isPending && <Loader2 size={12} className="animate-spin" />}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}

function AccessGroupMembersModal({
  group,
  onClose,
}: {
  group: AccessGroupRow;
  onClose: () => void;
}) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const usersQ = useQuery({ queryKey: ['settings-users'], queryFn: () => listUsers() });
  const membersQ = useQuery({
    queryKey: ['access-group-members', group.id],
    queryFn: () => getAccessGroupMembers(group.id),
  });

  const [search, setSearch] = useState('');
  // Seleção local. `null` enquanto não carregou os membros atuais (evita
  // salvar um conjunto vazio antes do fetch inicial terminar).
  const [selected, setSelected] = useState<Set<number> | null>(null);

  // Hidrata a seleção inicial com os membros atuais quando o fetch retorna.
  const initialMembers = membersQ.data?.user_ids;
  const sel = useMemo(() => {
    if (selected) return selected;
    if (initialMembers) return new Set(initialMembers);
    return new Set<number>();
  }, [selected, initialMembers]);

  const users = usersQ.data ?? [];
  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    if (!term) return users;
    return users.filter(
      (u) => u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term),
    );
  }, [users, search]);

  function toggle(id: number): void {
    const next = new Set(sel);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  const saveMut = useMutation({
    mutationFn: () => setAccessGroupMembers(group.id, Array.from(sel)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['access-groups'] });
      void qc.invalidateQueries({ queryKey: ['access-group-members', group.id] });
      void qc.invalidateQueries({ queryKey: ['settings-users'] });
      toast.success('Membros atualizados');
      onClose();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.problem.title : 'Falha ao salvar membros'),
  });

  const loading = usersQ.isLoading || membersQ.isLoading;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[80vh] w-[480px] flex-col rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex flex-shrink-0 items-center justify-between">
          <h3 className="font-bold text-[16px]">Membros do grupo «{group.name}»</h3>
          <button type="button" onClick={onClose} className="text-n-500" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>

        {/* Busca */}
        <div className="mt-4 flex-shrink-0">
          <div
            className="flex items-center gap-2 rounded-md border border-n-300 px-3"
            style={{ height: '36px', background: 'var(--bg-surface)' }}
          >
            <Search size={14} strokeWidth={1.75} className="flex-shrink-0 text-n-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou email…"
              className="flex-1 border-none bg-transparent text-[13px] text-n-900 outline-none"
            />
          </div>
        </div>

        {/* Contador */}
        <p className="mt-3 flex-shrink-0 text-[12px] text-n-500">
          {sel.size} {sel.size === 1 ? 'selecionado' : 'selecionados'}
        </p>

        {/* Lista de usuários */}
        <div className="mt-2 min-h-0 flex-1 overflow-y-auto rounded-md border border-n-200">
          {loading && (
            <div className="py-10 text-center">
              <Loader2 size={18} className="mx-auto animate-spin text-n-400" />
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="py-10 text-center text-[13px] text-n-500">
              Nenhum usuário encontrado.
            </div>
          )}
          {!loading &&
            filtered.map((u) => (
              <UserCheckboxRow
                key={u.id}
                user={u}
                checked={sel.has(u.id)}
                onToggle={() => toggle(u.id)}
              />
            ))}
        </div>

        <div className="mt-5 flex flex-shrink-0 justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saveMut.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending || loading}>
            {saveMut.isPending && <Loader2 size={12} className="animate-spin" />}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}

function UserCheckboxRow({
  user,
  checked,
  onToggle,
}: {
  user: UserRow;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-2.5 border-b border-n-100 px-3 py-2 text-left last:border-b-0 hover:bg-n-50"
    >
      <input
        type="checkbox"
        checked={checked}
        readOnly
        className="h-4 w-4 flex-shrink-0 accent-brand-500"
      />
      {/* Avatar (foto ou ícone). */}
      <span className="relative flex h-[32px] w-[32px] flex-shrink-0 items-center justify-center overflow-hidden rounded-pill bg-n-100 text-n-400">
        <User size={18} strokeWidth={1.5} />
        {user.avatar_url && (
          <img
            key={user.avatar_url}
            src={user.avatar_url}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-[13px] text-n-900">{user.name}</span>
        <span className="block truncate text-[11px] text-n-500">{user.email}</span>
      </span>
    </button>
  );
}
